/**
 * Live vault updates over the server's `/ws` endpoint.
 *
 * The server broadcasts `{type:'file-changed', path, changeKind, mtimeMs}`
 * frames whenever the filesystem watcher observes an external edit. This
 * hook keeps a native WebSocket open (with capped-backoff reconnect) and
 * hands validated events to the caller, which decides what to invalidate.
 *
 * The frame parser and URL builder are pure and exported for unit tests;
 * the hook itself no-ops in environments without `WebSocket` (node vitest).
 */

import { useEffect, useRef } from 'react';

export type FileChangeKind = 'add' | 'change' | 'unlink';

export interface FileChangedEvent {
  readonly path: string;
  readonly changeKind: FileChangeKind;
  readonly mtimeMs: number | null;
}

/** Parse one raw WS frame; returns null for hello/pong/garbage frames. */
export function parseFileChangedFrame(raw: unknown): FileChangedEvent | null {
  if (typeof raw !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const rec = parsed as Record<string, unknown>;
  if (rec.type !== 'file-changed') return null;
  if (typeof rec.path !== 'string' || rec.path === '') return null;
  const kind = rec.changeKind;
  if (kind !== 'add' && kind !== 'change' && kind !== 'unlink') return null;
  const mtimeMs = typeof rec.mtimeMs === 'number' ? rec.mtimeMs : null;
  return { path: rec.path, changeKind: kind, mtimeMs };
}

/** ws:// or wss:// URL for the `/ws` endpoint, derived from the page origin. */
export function wsUrlFor(loc: { readonly protocol: string; readonly host: string }): string {
  const scheme = loc.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${loc.host}/ws`;
}

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 10_000;

/**
 * Subscribe to live vault updates. `onEvent` may change between renders;
 * the latest callback is always invoked (ref pattern) without re-opening
 * the socket.
 */
export function useLiveUpdates(onEvent: (event: FileChangedEvent) => void): void {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (typeof WebSocket === 'undefined' || typeof window === 'undefined') return undefined;

    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let closed = false;

    const connect = (): void => {
      if (closed) return;
      socket = new WebSocket(wsUrlFor(window.location));
      socket.addEventListener('open', () => {
        attempts = 0;
      });
      socket.addEventListener('message', (ev: MessageEvent) => {
        const event = parseFileChangedFrame(ev.data);
        if (event !== null) handlerRef.current(event);
      });
      socket.addEventListener('close', () => {
        if (closed) return;
        attempts += 1;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempts - 1), RECONNECT_MAX_MS);
        retryTimer = setTimeout(connect, delay);
      });
      socket.addEventListener('error', () => {
        // 'close' always follows; reconnect handled there.
      });
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
      if (socket !== null) socket.close();
    };
  }, []);
}
